import { useState } from 'react';
import { useParams, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { EmptyState, KpiTile, Sparkline, TabPanel, TabStrip, Timeline, useTabPrefix, type TimelineEntry } from '@pazportal/ui';
import { Freshness } from '../components/Freshness';
import { useLocation, type LocationAsset, type LocationDeploymentDetail } from '../hooks/useLocation';
import { useLocationDevices, useLocationNetworkLive, formatAvailabilityPct } from '../hooks/useLocationDevices';
import {
  UPTIME_EXPECTED_REFRESH_MS,
  UPTIME_STALE_MS,
  useLocationUptime,
  useLocationUptimeDaily,
} from '../hooks/useLocationUptime';
import { UNIFI_EXPECTED_REFRESH_MS, UNIFI_STALE_MS, useUnifiSites } from '../hooks/useUnifiSites';
import { isOpenStatus, useTickets } from '../hooks/useTickets';
import { useDocumentDownload, useDocuments, type CustomerDocument, type DocumentCategory } from '../hooks/useDocuments';
import { useActivityFeed } from '../hooks/useActivityFeed';
import { useCustomerDailyReports } from '../hooks/useCustomerDailyReports';
import { useFieldTrips } from '../hooks/useFieldTrips';
import { SkeletonCard, SkeletonRow } from '../components/Skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { BrandLogo } from '../components/BrandLogo';
import { DOCUMENT_SECTIONS } from '../config/documentSections';
import { PropertyTripList } from '../components/PropertyTripList';
import { OnsiteTripCalendar } from '../components/OnsiteTripCalendar';
import { usePropertyCatalog } from '../hooks/usePropertyCatalog';
import { visitHistoryNote, visitsForProperty } from '../lib/propertyCatalog';
import { buildSiteTimeline } from '../lib/siteTimeline';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { directoryLinks } from '../lib/propertyDirectory';
import { ResourceMark } from '../components/ResourceMark';
import { DocumentOpenLink, DocumentOrigin } from '../components/DocumentLink';
import { LocationServices, LocationVisitHistory } from '../components/LocationOperations';
import { MissionControlNetworks } from '../components/MissionControlNetworks';
import type { ResourceMarkKey } from '../config/resourceMarks';
import {
  formatSolutionType,
  formatDate,
  formatDateTime,
  formatFileSize,
  timeAgo,
  daysBetween,
} from '../lib/formatters';

type Tab = 'overview' | 'systems' | 'network' | 'services' | 'documents' | 'activity' | 'reports';

const TABS: Array<{ key: Tab; label: MessageKey }> = [
  { key: 'overview', label: 'location.tab.overview' },
  { key: 'systems', label: 'location.tab.systems' },
  { key: 'network', label: 'location.tab.network' },
  { key: 'services', label: 'location.tab.services' },
  { key: 'documents', label: 'location.tab.documents' },
  { key: 'activity', label: 'location.tab.activity' },
  { key: 'reports', label: 'location.tab.reports' },
];

const DEPENDENCY_MAP: Record<string, string[]> = {
  digital_signage: ['managed_network'],
  pro_av: ['managed_network'],
  video_surveillance: ['managed_network'],
  pos_systems: ['managed_network'],
  it_cloud: ['managed_network'],
  smart_robotics: ['managed_network'],
  keytracer: ['managed_network', 'access_control'],
};

type DocFilter = 'all' | DocumentCategory;
// Same fifteen sections as the Documents page (config/documentSections.ts), plus "All".
const DOC_CATEGORIES: Array<{ value: DocFilter; key: MessageKey; hintKey?: MessageKey }> = [
  { value: 'all', key: 'documents.category.all' },
  ...DOCUMENT_SECTIONS,
];

const ACTOR_KEY: Record<string, MessageKey> = {
  system: 'dashboard.actor.system',
  paznetworks: 'dashboard.actor.paznetworks',
  customer: 'dashboard.actor.customer',
};

const ACTIVITY_PAGE_SIZE = 20;
const ROUTE_SECTIONS: Tab[] = ['systems', 'network', 'services', 'documents', 'activity', 'reports'];

export function LocationDetailPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const section = routerLocation.pathname.split('/').filter(Boolean).pop();
  const tab: Tab = section && (ROUTE_SECTIONS as string[]).includes(section) ? (section as Tab) : 'overview';
  const { data: location, isLoading, isError, error } = useLocation(id);
  const { data: tickets } = useTickets();
  const documentsQuery = useDocuments({ location_id: id });
  const devicesQuery = useLocationDevices(id);
  const { data: networkLive } = useLocationNetworkLive(id);
  const uptime = useLocationUptime(id);
  // The trend line comes from the server-side daily aggregate; the 24-hour read
  // above stays the source for the headline availability figure.
  // Keyed on the location as loaded — which is already filtered to the active
  // accounts — not on the raw route id. location_uptime_daily() admits staff for
  // any location, so a staff member viewing as one customer who opened another
  // customer's location URL would otherwise fetch that customer's uptime. The
  // page shows "not found" there, but the rule is to scope on the client and
  // never lean on the server's staff bypass.
  const uptimeMonth = useLocationUptimeDaily(location?.id, 30);
  const { data: unifiSites } = useUnifiSites(id);
  const reports = useCustomerDailyReports(id);
  const { data: fieldTrips } = useFieldTrips();
  const { catalog } = usePropertyCatalog();
  const tabPrefix = useTabPrefix();
  const download = useDocumentDownload();

  const [docCategory, setDocCategory] = useState<DocFilter>('all');
  const [activityPage, setActivityPage] = useState(0);
  const [downloadFailed, setDownloadFailed] = useState<{ id: string; message: string } | null>(null);

  const activity = useActivityFeed({
    locationId: id,
    customerId: location?.customer_id ?? null,
    limit: tab === 'activity' ? ACTIVITY_PAGE_SIZE : 5,
    page: tab === 'activity' ? activityPage : 0,
    enabled: !!location,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonCard />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !location) {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <EmptyState
          icon={<PinIcon />}
          title={t('location.not_found')}
          subtitle={isError && error instanceof Error ? error.message : t('location.not_found_hint')}
          action={{ label: t('location.back'), onClick: () => navigate('/locations') }}
        />
      </div>
    );
  }

  const deployments = location.deployments ?? [];
  const locationTickets = (tickets ?? []).filter((tk) => tk.location_id === location.id);
  const openTicketCount = locationTickets.filter((tk) => isOpenStatus(tk.status)).length;

  const now = Date.now();
  const slaEvaluated = locationTickets.filter(
    (tk) => tk.sla_deadline && (tk.resolved_at || new Date(tk.sla_deadline).getTime() < now),
  );
  const slaMet = slaEvaluated.filter((tk) => tk.resolved_at && tk.sla_deadline && tk.resolved_at <= tk.sla_deadline);
  const slaPct = slaEvaluated.length > 0 ? Math.round((slaMet.length / slaEvaluated.length) * 100) : null;

  const lastVisit = [...(fieldTrips ?? [])]
    .filter((trip) => trip.location_id === location.id && (trip.status === 'completed' || trip.status === 'in_progress'))
    .sort((a, b) => (b.end_date ?? b.start_date ?? '').localeCompare(a.end_date ?? a.start_date ?? ''))[0];
  const locationTrips = (fieldTrips ?? []).filter((trip) => trip.location_id === location.id);

  // PazNetworks' visit record for this property, read from property_directory
  // and property_visit_history and keyed by the registry link. A site with no
  // link shows no record: the catalog query is scoped to the codes the
  // account's locations carry, so a name guess could not resolve one anyway.
  // The fix is an alias in the registry. Deployed solutions come from
  // `deployments` rows.
  const catalogCode = location.directory?.code ?? null;
  const catalogVisits = visitsForProperty(catalog, catalogCode);
  const catalogNote = visitHistoryNote(catalog, catalogCode);

  const cityState = [location.address?.city ?? location.directory?.city, location.address?.state ?? location.directory?.state]
    .filter(Boolean)
    .join(', ');

  const links = directoryLinks(location.directory);
  type ResourceChip = { label: string; href: string | null; kind: ResourceMarkKey };
  const resourceLinks: ResourceChip[] = (
    [
      { label: t('location.tab.network'), href: `/locations/${location.id}/network`, kind: 'missionControl' },
      { label: 'UniFi', href: links.unifi, kind: 'unifi' },
      { label: 'Auvik', href: links.auvik, kind: 'auvik' },
      { label: 'PazNet Learn', href: links.learn, kind: 'learn' },
      { label: t('property.resources.engineering'), href: links.engineering, kind: 'engineering' },
      { label: 'Wi-Q', href: links.ordering, kind: 'ordering' },
      { label: t('property.resources.rankings_title'), href: links.rankings, kind: 'rankings' },
      ...links.additionalNetworks.flatMap<ResourceChip>((n) => [
        { label: `${n.name} · ${t('location.tab.network')}`, href: n.missionControl ? `/locations/${location.id}/network` : null, kind: 'missionControl' },
        { label: `${n.name} · UniFi`, href: n.unifi, kind: 'unifi' },
      ]),
    ] satisfies ResourceChip[]
  ).filter((l) => !!l.href);

  const unifiLatest = (unifiSites ?? []).reduce<string | null>(
    (max, s) => (s.updated_at && (!max || s.updated_at > max) ? s.updated_at : max),
    null,
  );
  const auvikDevices = devicesQuery.data ?? [];
  const auvikConnected = auvikDevices.length > 0;

  const filteredDocs =
    docCategory === 'all' ? documentsQuery.data : documentsQuery.data?.filter((d) => d.category === docCategory);

  const startDownload = (doc: CustomerDocument) => {
    setDownloadFailed(null);
    download.mutate(doc, { onError: (err) => setDownloadFailed({ id: doc.id, message: err.message }) });
  };

  const uptimeData = uptime.data;

  // One point per day over the month, newest last. Drawn as a mean; the day's
  // worst sample rides along in the label so an outage inside an otherwise
  // healthy day is still visible on hover.
  const uptimeTrendPoints = (() => {
    const daily = uptimeMonth.data ?? [];
    return daily.map((point) => ({
      label:
        point.worstPct < point.pct - 1
          ? t('location.availability_trend_point_dip', {
              date: formatDate(point.day),
              worst: point.worstPct.toFixed(0),
            })
          : formatDate(point.day),
      value: point.pct,
    }));
  })();

  // The property's story in one rail: what PazNetworks delivered, what is
  // scheduled, and what the customer has planned.
  const visitTimeline: TimelineEntry[] = buildSiteTimeline({
    visits: catalogVisits,
    trips: locationTrips,
    labels: {
      delivered: t('location.timeline.delivered'),
      scheduled: t('location.timeline.scheduled'),
      onSite: t('location.timeline.on_site'),
    },
    formatDate,
    todayIso: new Date().toISOString().slice(0, 10),
  });
  const availability = uptimeData?.availabilityPct ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 sm:items-center">
        <button
          type="button"
          onClick={() => navigate('/locations')}
          aria-label={t('location.back')}
          className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-text-faint hover:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-xl font-semibold text-text">{location.name}</h1>
            {location.directory?.code && (
              <span className="font-mono text-xs text-text-faint">{location.directory.code}</span>
            )}
            {location.directory?.brand && <BrandLogo brand={location.directory.brand} name={location.name} className="h-7" />}
          </div>
          {cityState && <p className="text-sm text-text-muted">{cityState}</p>}
        </div>
      </div>

      <TabStrip
        idPrefix={tabPrefix}
        label={location.name}
        value={tab}
        onChange={(key) => {
          if (!id) return;
          navigate(key === 'overview' ? `/locations/${id}` : `/locations/${id}/${key}`);
          setActivityPage(0);
        }}
        tabs={TABS.map((item) => ({ key: item.key, label: t(item.label) }))}
      />

      {tab === 'overview' && (
        <TabPanel idPrefix={tabPrefix} tabKey="overview">
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            <KpiTile
              label={t('location.availability')}
              value={formatAvailabilityPct(availability)}
              detail={
                uptimeData && uptimeData.samples.length > 0
                  ? `${t('location.availability_latest', {
                      online: uptimeData.latestOnline ?? 0,
                      total: uptimeData.latestTotal ?? 0,
                    })} · ${t('location.availability_samples', { n: uptimeData.samples.length })}`
                  : t('location.availability_none')
              }
              onClick={() => id && navigate(`/locations/${id}/network`)}
              lastUpdatedAt={uptimeData?.latestSampledAt ?? null}
              expectedRefreshMs={UPTIME_EXPECTED_REFRESH_MS}
              staleMs={UPTIME_STALE_MS}
              freshnessLabel={t('location.availability')}
              className="sm:col-span-2"
            >
              {/* A month of daily means. The 90% band marks the floor below
                  which a day is not an acceptable day. */}
              <Sparkline
                points={uptimeTrendPoints}
                title={t('location.availability_trend')}
                format={(value) => `${value.toFixed(1)}%`}
                thresholdValue={90}
                emptyLabel={t('location.availability_trend_empty')}
              />
            </KpiTile>

            <KpiTile
              label={t('location.sla')}
              value={slaPct === null ? t('common.none') : `${slaPct}%`}
              tone={slaPct === null ? 'default' : slaPct >= 99 ? 'good' : slaPct >= 95 ? 'warning' : 'critical'}
              detail={
                slaPct === null
                  ? t('dashboard.sla_none')
                  : t('dashboard.sla_met', { met: slaMet.length, total: slaEvaluated.length })
              }
              onClick={() => navigate(id ? `/locations/${id}/tickets` : '/tickets')}
            />

            <KpiTile
              label={t('location.open_tickets')}
              value={openTicketCount}
              tone={openTicketCount > 0 ? 'warning' : 'default'}
              detail={lastVisit ? t('location.last_visit_on', {
                date: formatDate(lastVisit.end_date ?? lastVisit.start_date),
              }) : t('location.no_visits')}
              onClick={() => navigate(id ? `/locations/${id}/tickets` : '/tickets')}
            />
          </div>

          <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="systems-overview-heading">
            <h3 id="systems-overview-heading" className="mb-3 text-sm font-medium text-text-muted">{t('location.systems')}</h3>
            {deployments.length === 0 ? (
              <div>
                <p className="text-sm text-text">{t('location.systems_none')}</p>
                <p className="mt-1 text-xs text-text-faint">{t('location.systems_none_hint')}</p>
              </div>
            ) : (
              <div className="divide-y divide-divider">
                {deployments.map((d) => (
                  <button
                    type="button"
                    key={d.id}
                    onClick={() => id && navigate(`/locations/${id}/systems`)}
                    className="flex min-h-11 w-full items-center justify-between gap-3 py-2 text-left hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="truncate text-sm font-medium text-text">{formatSolutionType(d.solution_type)}</span>
                      {d.vendor && <span className="text-xs text-text-faint">{d.vendor}</span>}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-3">
                      <StatusBadge status={d.status ?? 'unknown'} />
                      <span className="text-xs text-text-faint">
                        {d.last_health_check ? timeAgo(d.last_health_check) : t('location.no_check')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <OnsiteTripCalendar
            key={location.id}
            customerId={location.customer_id}
            visits={catalogVisits}
            visitNote={catalogNote}
            liveTrips={locationTrips}
            locations={[{ id: location.id, name: location.name }]}
          />

          <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="site-history-heading">
            <div className="mb-3">
              <h3 id="site-history-heading" className="text-sm font-medium text-text-muted">
                {t('location.timeline.title')}
              </h3>
              <p className="mt-0.5 text-xs text-text-faint">{t('location.timeline.subtitle')}</p>
            </div>
            <Timeline entries={visitTimeline} emptyLabel={t('location.timeline.empty')} />
          </section>

          <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="resources-heading">
            <h3 id="resources-heading" className="mb-3 text-sm font-medium text-text-muted">{t('location.resources')}</h3>
            {!location.directory ? (
              <p className="text-sm text-text-muted">{t('property.resources.no_directory')}</p>
            ) : resourceLinks.length === 0 ? (
              <p className="text-sm text-text-muted">{t('property.resources.not_linked')}</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {resourceLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border bg-surface-2 px-3 text-sm text-primary hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-9"
                    >
                      <ResourceMark kind={link.kind} size="sm" />
                      {link.label}
                      <span aria-hidden="true">↗</span>
                      <span className="sr-only"> {t('property.resources.new_tab')}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {location.directory && <p className="mt-3 text-xs text-text-faint">{t('property.resources.sign_in')}</p>}
          </section>

          <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="activity-overview-heading">
            <h3 id="activity-overview-heading" className="mb-3 text-sm font-medium text-text-muted">{t('dashboard.recent_activity')}</h3>
            <ActivityList
              isLoading={activity.isLoading}
              error={activity.error}
              events={(activity.data?.events ?? []).slice(0, 5)}
              compact
            />
          </section>
        </div>
        </TabPanel>
      )}

      {tab === 'systems' && (
        <TabPanel idPrefix={tabPrefix} tabKey="systems">
        <div className="space-y-4">
          {deployments.length === 0 ? (
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState icon={<SystemsIcon />} title={t('location.systems_none')} subtitle={t('location.systems_none_hint')} />
            </div>
          ) : (
            deployments.map((d) => <DeploymentCard key={d.id} deployment={d} />)
          )}
        </div>
        </TabPanel>
      )}

      {tab === 'network' && (
        <TabPanel idPrefix={tabPrefix} tabKey="network">
        <div className="space-y-4">
          <MissionControlNetworks locationId={location.id} />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-text-muted">{t('location.network.unifi_hint')}</p>
            {(unifiSites ?? []).length > 0 && (
              <Freshness
                lastUpdatedAt={unifiLatest}
                label={t('location.network.source_unifi')}
                expectedRefreshMs={UNIFI_EXPECTED_REFRESH_MS}
                staleMs={UNIFI_STALE_MS}
              />
            )}
          </div>

          <section aria-labelledby="unifi-heading" className="space-y-3">
            <h2 id="unifi-heading" className="text-sm font-semibold text-text">{t('location.network.unifi_title')}</h2>
            {(unifiSites ?? []).length === 0 ? (
              <div className="rounded-lg border border-border bg-surface">
                <EmptyState icon={<WifiIcon />} title={t('location.network.unifi_none')} subtitle={t('location.network.unifi_none_hint')} />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {(unifiSites ?? []).map((site) => {
                  const online = site.total_devices !== null ? Math.max(site.total_devices - (site.offline_devices ?? 0), 0) : null;
                  return (
                    <div key={site.id} className="rounded-lg border border-border bg-surface p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-text">{site.site_name ?? t('location.network.site')}</p>
                          {site.unifi_host_name && <p className="truncate text-xs text-text-muted">{site.unifi_host_name}</p>}
                        </div>
                        {site.updated_at && (
                          <span className="flex-shrink-0 text-xs text-text-faint">{t('common.updated')} {timeAgo(site.updated_at)}</span>
                        )}
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                        <Fact label={t('location.network.devices')}>
                          {site.total_devices !== null ? `${online} / ${site.total_devices} ${t('location.network.online')}` : t('common.none')}
                        </Fact>
                        <Fact label={t('location.network.offline')}>{site.offline_devices ?? t('common.none')}</Fact>
                        <Fact label={t('location.network.gateways')}>
                          {site.gateway_devices !== null
                            ? `${Math.max(site.gateway_devices - (site.offline_gateway_devices ?? 0), 0)} / ${site.gateway_devices}`
                            : t('common.none')}
                        </Fact>
                        <Fact label={t('location.network.clients')}>
                          {site.wifi_client_count !== null || site.wired_client_count !== null
                            ? `${site.wifi_client_count ?? 0} ${t('location.network.wifi')} · ${site.wired_client_count ?? 0} ${t('location.network.wired')}${
                                site.guest_clients !== null ? ` · ${site.guest_clients} ${t('location.network.guest')}` : ''
                              }`
                            : t('common.none')}
                        </Fact>
                        <Fact label={t('location.network.ssids')}>{site.ssid_count ?? t('common.none')}</Fact>
                        <Fact label={t('location.network.wan')}>{site.wan_links ?? t('common.none')}</Fact>
                        <Fact label={t('location.network.pending')}>{site.pending_update_devices ?? t('common.none')}</Fact>
                        <Fact label={t('location.network.critical')}>
                          <span className={(site.critical_notifications ?? 0) > 0 ? 'font-medium text-notification' : ''}>
                            {site.critical_notifications ?? t('common.none')}
                          </span>
                        </Fact>
                        <Fact label={t('location.network.timezone')}>{site.timezone ?? t('common.none')}</Fact>
                      </dl>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <NetworkFactCard
            title={t('location.network.ssids_title')}
            source={t('location.network.source_unifi')}
            empty={t('location.network.none_ssids')}
            rows={(networkLive?.wlans ?? []).map((w, i) => ({
              id: w.id ?? `wlan-${i}`,
              primary: w.name ?? 'SSID',
              secondary:
                [
                  w.security,
                  w.enabled === false ? t('location.network.disabled') : w.enabled === true ? t('location.network.enabled') : null,
                  w.hidden ? t('location.network.hidden') : null,
                ]
                  .filter(Boolean)
                  .concat(w.updated_at ? [`${t('location.device.last_seen')} ${timeAgo(w.updated_at)}`] : [])
                  .join(' · ') || undefined,
            }))}
          />
          <NetworkFactCard
            title={t('location.network.aps')}
            source={t('location.network.source_unifi')}
            empty={t('location.network.none_aps')}
            rows={(networkLive?.accessPoints ?? []).map((ap, i) => ({
              id: ap.id ?? `ap-${i}`,
              primary: ap.name ?? ap.model ?? 'AP',
              secondary: [ap.model, ap.mac_address, ap.ip_address, ap.updated_at ? `${t('location.device.last_seen')} ${timeAgo(ap.updated_at)}` : null].filter(Boolean).join(' · ') || undefined,
            }))}
          />
          <NetworkFactCard
            title={t('location.network.clients_title')}
            source={t('location.network.source_unifi')}
            empty={t('location.network.none_clients')}
            rows={(networkLive?.clients ?? []).map((c, i) => ({
              id: c.id ?? `client-${i}`,
              primary: c.name ?? c.mac_address ?? 'Client',
              secondary: [c.ssid, c.connection_type, c.ip_address, c.updated_at ? `${t('location.device.last_seen')} ${timeAgo(c.updated_at)}` : null].filter(Boolean).join(' · ') || undefined,
            }))}
          />

          <section aria-labelledby="auvik-heading" className="space-y-3">
            <h2 id="auvik-heading" className="text-sm font-semibold text-text">{t('location.network.auvik_title')}</h2>
            {devicesQuery.isLoading ? (
              <div className="rounded-lg border border-border bg-surface p-5">
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </div>
            ) : !auvikConnected ? (
              <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
                <p className="text-sm font-medium text-text">{t('location.network.auvik_not_connected')}</p>
                <p className="mt-1 text-xs text-text-faint">{t('location.network.auvik_not_connected_hint')}</p>
              </div>
            ) : (
              <>
                <DeviceTable devices={auvikDevices} />
                <NetworkFactCard
                  title={t('location.network.alerts')}
                  source={t('location.network.source_auvik')}
                  empty={t('location.network.alerts_none')}
                  rows={(networkLive?.alerts ?? []).map((a, i) => ({
                    id: a.id ?? `alert-${i}`,
                    primary: a.alert_name ?? a.entity_name ?? 'Alert',
                    secondary: [a.entity_name, a.message].filter(Boolean).join(' · ') || undefined,
                    badge: a.severity,
                  }))}
                />
                <NetworkFactCard
                  title={t('location.network.interfaces')}
                  source={t('location.network.source_auvik')}
                  empty={t('common.none')}
                  rows={(networkLive?.interfaces ?? []).map((iface, i) => ({
                    id: iface.id ?? `iface-${i}`,
                    primary: iface.name ?? 'Interface',
                    secondary: [iface.interface_type, iface.ip_address, iface.negotiated_speed].filter(Boolean).join(' · ') || undefined,
                    badge: iface.operational_status,
                  }))}
                />
              </>
            )}
          </section>
        </div>
        </TabPanel>
      )}

      {tab === 'services' && (
        <TabPanel idPrefix={tabPrefix} tabKey="services">
          <LocationServices locationId={location.id} />
        </TabPanel>
      )}

      {tab === 'documents' && (
        <TabPanel idPrefix={tabPrefix} tabKey="documents">
        <div className="space-y-4">
          {/* The property's Learn manuals, surfaced as first-class cards rather
              than buried in the resource link row. Zoho Learn sends
              X-Frame-Options: SAMEORIGIN, so these cannot be embedded in an
              iframe — the link is labelled as leaving the portal instead of
              rendering a blocked frame. */}
          {(links.learn || links.engineering) && (
            <section
              className="rounded-lg border border-border bg-surface p-4 sm:p-5"
              aria-labelledby="site-manuals-heading"
            >
              <div className="mb-3">
                <h3 id="site-manuals-heading" className="text-sm font-medium text-text-muted">
                  {t('location.manuals.title')}
                </h3>
                <p className="mt-0.5 text-xs text-text-faint">{t('location.manuals.subtitle')}</p>
              </div>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { href: links.learn, title: t('location.manuals.property'), body: t('location.manuals.property_hint') },
                  { href: links.engineering, title: t('location.manuals.engineering'), body: t('location.manuals.engineering_hint') },
                ]
                  .filter((card): card is { href: string; title: string; body: string } => !!card.href)
                  .map((card) => (
                    <li key={card.title}>
                      <a
                        href={card.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-full flex-col rounded-md border border-border bg-surface-2 p-3 hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-text">{card.title}</span>
                          <svg
                            aria-hidden="true"
                            className="h-4 w-4 shrink-0 text-primary"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M7 7h10v10" />
                          </svg>
                        </span>
                        <span className="mt-1 text-xs leading-relaxed text-text-muted">{card.body}</span>
                        <span className="mt-2 text-[11px] text-text-faint">
                          {t('location.manuals.opens_in_learn')}
                        </span>
                      </a>
                    </li>
                  ))}
              </ul>
            </section>
          )}

          {/* Filters, not tabs — see DocumentsPage for the same reasoning. */}
          <div role="radiogroup" aria-label={t('documents.filter_label')} className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1 sm:mx-0 sm:px-0">
            {DOC_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                role="radio"
                aria-checked={docCategory === cat.value}
                onClick={() => setDocCategory(cat.value)}
                title={cat.hintKey ? t(cat.hintKey) : undefined}
                className={`min-h-11 whitespace-nowrap rounded-lg px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-9 ${
                  docCategory === cat.value ? 'bg-primary text-text-inverse' : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
                }`}
              >
                {t(cat.key)}
              </button>
            ))}
          </div>

          {documentsQuery.isLoading ? (
            <div className="rounded-lg border border-border bg-surface p-5">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : documentsQuery.isError ? (
            <p role="alert" className="rounded-lg border border-notification bg-notification-soft p-4 text-sm text-notification">
              {t('documents.error')} {documentsQuery.error instanceof Error ? documentsQuery.error.message : ''}
            </p>
          ) : !filteredDocs?.length ? (
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState
                icon={<DocIcon />}
                title={
                  docCategory === 'all'
                    ? t('documents.empty')
                    : t('documents.empty_category', {
                        category: t(DOC_CATEGORIES.find((c) => c.value === docCategory)!.key).toLowerCase(),
                      })
                }
                subtitle={t('documents.empty_hint')}
              />
            </div>
          ) : (
            <ul className="divide-y divide-divider rounded-lg border border-border bg-surface">
              {filteredDocs.map((doc) => {
                const isBusy = download.isPending && download.variables?.id === doc.id;
                return (
                  <li key={doc.id} className="px-4 py-3 sm:px-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <DocIcon small />
                        <div className="min-w-0">
                          <p className="truncate text-sm text-text">{doc.title}</p>
                          <p className="text-xs text-text-faint">
                            <DocumentOrigin doc={doc} fileSize={formatFileSize(doc.file_size_bytes)} /> &middot;{' '}
                            {formatDate(doc.created_at)}
                          </p>
                        </div>
                      </div>
                      {doc.external_url ? (
                        <DocumentOpenLink href={doc.external_url} compact />
                      ) : doc.s3_key && (
                        <button
                          type="button"
                          onClick={() => startDownload(doc)}
                          disabled={isBusy}
                          className="inline-flex min-h-11 flex-shrink-0 items-center px-2 text-sm font-medium text-primary hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
                        >
                          {isBusy ? t('common.loading') : t('documents.download')}
                        </button>
                      )}
                    </div>
                    {downloadFailed?.id === doc.id && (
                      <p role="alert" className="mt-1 text-xs text-notification">
                        {t('documents.download_failed')}: {downloadFailed.message}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        </TabPanel>
      )}

      {tab === 'activity' && (
        <TabPanel idPrefix={tabPrefix} tabKey="activity">
        <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
          <LocationVisitHistory locationId={location.id} />
          {catalogVisits.length > 0 && (
            <section className="mb-4 border-b border-border pb-4" aria-labelledby="catalog-trips-heading">
              <h3 id="catalog-trips-heading" className="mb-2 text-sm font-medium text-text-muted">
                {t('property.trips.title')} ({catalogVisits.length})
              </h3>
              <PropertyTripList
                visits={catalogVisits}
                note={catalogNote}
                preview={8}
                earlierLabel={t('property.trips.earlier')}
              />
            </section>
          )}
          <ActivityList isLoading={activity.isLoading} error={activity.error} events={activity.data?.events ?? []} />
          {(activity.data?.totalCount ?? 0) > ACTIVITY_PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
              <button
                type="button"
                onClick={() => setActivityPage((p) => Math.max(0, p - 1))}
                disabled={activityPage === 0}
                className="min-h-11 px-2 text-sm text-text-muted hover:text-text disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <span className="text-xs text-text-faint">
                {t('common.page_of', {
                  page: activityPage + 1,
                  total: Math.ceil((activity.data?.totalCount ?? 0) / ACTIVITY_PAGE_SIZE),
                })}
              </span>
              <button
                type="button"
                onClick={() => setActivityPage((p) => p + 1)}
                disabled={(activityPage + 1) * ACTIVITY_PAGE_SIZE >= (activity.data?.totalCount ?? 0)}
                className="min-h-11 px-2 text-sm text-text-muted hover:text-text disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
        </TabPanel>
      )}

      {tab === 'reports' && (
        <TabPanel idPrefix={tabPrefix} tabKey="reports">
        <div className="space-y-3">
          <p className="text-xs text-text-muted">{t('location.reports_hint')}</p>
          {reports.isLoading ? (
            <div className="space-y-2 rounded-lg border border-border bg-surface p-5">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : reports.isError ? (
            <p role="alert" className="rounded-lg border border-notification bg-notification-soft p-4 text-sm text-notification">
              {t('location.reports_error')} {reports.error instanceof Error ? reports.error.message : ''}
            </p>
          ) : !reports.data?.length ? (
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState icon={<ReportIcon />} title={t('location.reports_empty')} subtitle={t('location.reports_empty_hint')} />
            </div>
          ) : (
            <div className="divide-y divide-divider rounded-lg border border-border bg-surface">
              {reports.data.map((report) => (
                <details key={report.id} className="px-4 py-3 sm:px-5">
                  <summary className="flex min-h-11 cursor-pointer items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text">
                        {t('location.report.day', { n: report.day_number })} — {formatDate(report.report_date)}
                      </p>
                      <p className="mt-0.5 text-xs text-text-faint">
                        {report.crew_on_site ?? t('location.report.crew')}
                        {report.hours_on_site ? ` · ${report.hours_on_site}` : ''}
                        {report.delivered_to_customer_at
                          ? ` · ${t('location.report.delivered', { ago: timeAgo(report.delivered_to_customer_at) })}`
                          : ''}
                      </p>
                    </div>
                    <svg className="h-4 w-4 flex-shrink-0 text-text-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="mt-3 space-y-3 text-sm text-text-muted">
                    {report.work_completed && (
                      <div>
                        <p className="mb-1 text-xs uppercase tracking-wide text-text-faint">{t('location.report.work')}</p>
                        <p className="whitespace-pre-wrap text-text">{report.work_completed}</p>
                      </div>
                    )}
                    {report.tomorrow_plan && (
                      <div>
                        <p className="mb-1 text-xs uppercase tracking-wide text-text-faint">{t('location.report.plan')}</p>
                        <p className="whitespace-pre-wrap text-text">{report.tomorrow_plan}</p>
                      </div>
                    )}
                    {!report.work_completed && !report.tomorrow_plan && (
                      <p className="text-xs italic text-text-faint">{t('location.report.no_content')}</p>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
        </TabPanel>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-text-faint">{label}</dt>
      <dd className="mt-0.5 text-text">{children}</dd>
    </div>
  );
}

function ActivityList({
  isLoading,
  error,
  events,
  compact = false,
}: {
  isLoading: boolean;
  error: unknown;
  events: Array<{ id: string; summary: string; occurred_at: string; actor_kind: string }>;
  compact?: boolean;
}) {
  const { t } = useI18n();
  if (isLoading) {
    return <>{Array.from({ length: compact ? 3 : 8 }).map((_, i) => <SkeletonRow key={i} />)}</>;
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-notification">
        {t('location.activity_error')} {error instanceof Error ? error.message : ''}
      </p>
    );
  }
  if (events.length === 0) {
    return <p className="py-2 text-sm text-text-faint">{t('location.activity_empty')}</p>;
  }
  return (
    <ul className="divide-y divide-divider">
      {events.map((event) => (
        <li key={event.id} className="flex items-start justify-between gap-3 py-2.5 sm:items-center">
          <div className="min-w-0">
            <p className="text-sm text-text">{event.summary}</p>
            <p className="text-xs text-text-faint">
              {compact ? timeAgo(event.occurred_at) : formatDateTime(event.occurred_at)} ·{' '}
              {t(ACTOR_KEY[event.actor_kind] ?? 'dashboard.actor.system')}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DeploymentCard({ deployment: d }: { deployment: LocationDeploymentDetail }) {
  const { t } = useI18n();
  const assets = d.assets ?? [];
  const deps = DEPENDENCY_MAP[d.solution_type] ?? [];
  const dependedUponBy = Object.entries(DEPENDENCY_MAP)
    .filter(([, v]) => v.includes(d.solution_type))
    .map(([k]) => k);

  return (
    <div className="rounded-lg border border-border bg-surface">
      <div className="flex flex-col gap-2 rounded-t-lg border-b border-border bg-surface-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex items-center gap-3 sm:gap-4">
          <span className="text-sm font-medium text-text">{formatSolutionType(d.solution_type)}</span>
          <StatusBadge status={d.status ?? 'unknown'} />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
          {d.sla_response_hours !== null && <span>{t('location.sla_response', { n: d.sla_response_hours })}</span>}
          {d.commissioned_date && <span>{t('location.since', { date: formatDate(d.commissioned_date) })}</span>}
        </div>
      </div>
      <div className="p-4 sm:p-5">
        <p className="mb-3 text-xs text-text-muted">{t('location.inventory_hint')}</p>
        {assets.length === 0 ? (
          <p className="text-sm text-text-faint">{t('location.no_assets')}</p>
        ) : (
          <>
            <ul className="space-y-3 md:hidden">
              {assets.map((a) => (
                <li key={a.id} className="rounded-md border border-border bg-surface-2 p-3 text-sm">
                  <p className="font-medium text-text">{a.device_name ?? a.asset_id_label ?? t('common.none')}</p>
                  <p className="text-xs text-text-muted">{[a.make, a.model].filter(Boolean).join(' ') || t('common.none')}</p>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <Fact label={t('location.asset.serial')}><span className="font-mono">{a.serial_number ?? t('common.none')}</span></Fact>
                    <Fact label={t('location.asset.ip')}><span className="font-mono">{a.ip_address ?? t('common.none')}</span></Fact>
                    <Fact label={t('location.asset.firmware')}>{a.firmware_version ?? t('common.none')}</Fact>
                    <Fact label={t('location.asset.warranty')}><WarrantyCell asset={a} /></Fact>
                  </dl>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-text-muted">
                    <th scope="col" className="pb-2 font-medium">{t('location.asset.device')}</th>
                    <th scope="col" className="pb-2 font-medium">{t('location.asset.make_model')}</th>
                    <th scope="col" className="pb-2 font-medium">{t('location.asset.serial')}</th>
                    <th scope="col" className="pb-2 font-medium">{t('location.asset.ip')}</th>
                    <th scope="col" className="pb-2 font-medium">{t('location.asset.firmware')}</th>
                    <th scope="col" className="pb-2 font-medium">{t('location.asset.warranty')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {assets.map((a) => (
                    <tr key={a.id} className="text-text">
                      <td className="py-2">{a.device_name ?? a.asset_id_label ?? t('common.none')}</td>
                      <td className="py-2">{[a.make, a.model].filter(Boolean).join(' ') || t('common.none')}</td>
                      <td className="py-2 font-mono text-xs">{a.serial_number ?? t('common.none')}</td>
                      <td className="py-2 font-mono text-xs">{a.ip_address ?? t('common.none')}</td>
                      <td className="py-2 text-xs">{a.firmware_version ?? t('common.none')}</td>
                      <td className="py-2"><WarrantyCell asset={a} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {(deps.length > 0 || dependedUponBy.length > 0) && (
          <div className="mt-4 border-t border-border pt-3 text-xs text-text-muted">
            {deps.length > 0 && <p>{t('location.depends_on')}: {deps.map(formatSolutionType).join(', ')}</p>}
            {dependedUponBy.length > 0 && <p>{t('location.depended_by')}: {dependedUponBy.map(formatSolutionType).join(', ')}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function WarrantyCell({ asset }: { asset: LocationAsset }) {
  const { t } = useI18n();
  if (!asset.warranty_expiry) return <>{t('common.none')}</>;
  const days = daysBetween(new Date().toISOString(), asset.warranty_expiry);
  return <span className={days < 90 ? 'font-medium text-notification' : ''}>{formatDate(asset.warranty_expiry)}</span>;
}

function DeviceTable({ devices }: { devices: ReturnType<typeof useLocationDevices>['data'] & object }) {
  const { t } = useI18n();
  const staleAfterMs = 15 * 60 * 1000;
  const rows = devices.map((device) => {
    const age = device.last_seen_at ? Date.now() - new Date(device.last_seen_at).getTime() : null;
    return { device, stale: age !== null && age > staleAfterMs };
  });
  return (
    <div className="rounded-lg border border-border bg-surface">
      <ul className="divide-y divide-divider md:hidden">
        {rows.map(({ device, stale }) => (
          <li key={device.id} className="px-4 py-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="font-medium text-text">{device.name ?? t('common.none')}</p>
              {device.status ? <StatusBadge status={device.status} /> : null}
            </div>
            {device.make_model && <p className="text-xs text-text-muted">{device.make_model}</p>}
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <Fact label={t('location.device.type')}>{device.device_type ?? t('common.none')}</Fact>
              <Fact label={t('location.device.availability')}>{formatAvailabilityPct(device.availability_pct)}</Fact>
              <Fact label={t('location.device.ip')}><span className="font-mono">{device.ip_address ?? t('common.none')}</span></Fact>
              <Fact label={t('location.device.firmware')}>{device.firmware_version ?? t('common.none')}</Fact>
              <Fact label={t('location.device.last_seen')}>
                <span className={stale ? 'text-warning' : ''}>{device.last_seen_at ? formatDateTime(device.last_seen_at) : t('common.none')}</span>
              </Fact>
            </dl>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto p-5 md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-text-muted">
              <th scope="col" className="pb-2 font-medium">{t('location.device.device')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.type')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.status')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.availability')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.vendor')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.ip')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.firmware')}</th>
              <th scope="col" className="pb-2 font-medium">{t('location.device.last_seen')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {rows.map(({ device, stale }) => (
              <tr key={device.id} className="text-text">
                <td className="py-2">
                  <p>{device.name ?? t('common.none')}</p>
                  {device.make_model && <p className="text-xs text-text-muted">{device.make_model}</p>}
                </td>
                <td className="py-2">{device.device_type ?? t('common.none')}</td>
                <td className="py-2">{device.status ? <StatusBadge status={device.status} /> : t('common.none')}</td>
                <td className="py-2">{formatAvailabilityPct(device.availability_pct)}</td>
                <td className="py-2 text-xs">{device.vendor_name ?? t('common.none')}</td>
                <td className="py-2 font-mono text-xs">{device.ip_address ?? t('common.none')}</td>
                <td className="py-2 text-xs">{device.firmware_version ?? t('common.none')}</td>
                <td className={`py-2 text-xs ${stale ? 'text-warning' : ''}`}>
                  {device.last_seen_at ? formatDateTime(device.last_seen_at) : t('common.none')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NetworkFactCard({
  title,
  source,
  empty,
  rows,
}: {
  title: string;
  source: string;
  empty: string;
  rows: Array<{ id: string; primary: string; secondary?: string; badge?: string | null }>;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-label={title}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-text">{title}</h2>
        <p className="text-xs text-text-muted">{source}</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-text-muted">{empty}</p>
      ) : (
        <ul className="divide-y divide-divider">
          {rows.map((row) => (
            <li key={row.id} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-text">{row.primary}</p>
                {row.secondary && <p className="truncate text-xs text-text-muted">{row.secondary}</p>}
              </div>
              {row.badge && <StatusBadge status={row.badge} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PinIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    </svg>
  );
}

function SystemsIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function WifiIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
    </svg>
  );
}

function DocIcon({ small = false }: { small?: boolean }) {
  return (
    <svg className={small ? 'h-5 w-5 flex-shrink-0 text-text-faint' : 'h-12 w-12'} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
